import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, memo, useMemo, useCallback } from 'react';

import { useRelevancyOptions } from '../store';

import type { BaseCardProps } from './BaseCardComponent';
import BaseCardComponent from './BaseCardComponent';

import { getEditorialByTagQuery } from '@queries/editorial';
import { trackCardElement } from '@services/GoogleTagManagerService';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import { useTranslateFn } from '@shared/Translate';
import type { MediaImage } from '@shared-types/editorial.types';
import { language } from '@utilities/browser';
import { competitionFolder } from '@utilities/WindowContext';

export type SandEComponentProps = BaseCardProps & {
  cardType: string;
  tag?: string;
  logoByLanguage?: boolean;
  fetchArticle?: boolean;
  showTitle?: boolean;
  showButton?: boolean;
  trackArticleId?: boolean;
  trackingData?: {
    id?: string;
    name?: string;
    category?: string;
  };
};

const SandEComponent = (props: SandEComponentProps) => {
  const { options } = useRelevancyOptions();
  const t = useTranslateFn();

  const getTextFallBackTranslation = useCallback(
    (cardType: string) => {
      const logoTxtFallbackTag = `${options.competitionCode}_${cardType.replaceAll('-', '_')}`;
      let translation = t(logoTxtFallbackTag);
      if (translation === logoTxtFallbackTag) {
        translation = t(cardType.replaceAll('-', '_'));
      }
      return translation;
    },
    [options.competitionCode, t],
  );

  const {
    item: { teaser, subType },
    isVideoPlayer,
    video,
    cardType = subType?.toLowerCase() ?? '',
    fetchArticle = true,
    tag,
    target = '_self',
    logoByLanguage = true,
    logoImg = `${window.imgBaseUrl}/imgml/uefacom/cards/${cardType}/${competitionFolder}-${cardType}${
      logoByLanguage ? `-${language}` : ''
    }.svg`,
    logoTxtFallback,
    fallbackImg = `${window.imgBaseUrl}/imgml/uefacom/cards/${cardType}/bg-${competitionFolder}-${cardType}.jpg`,
    title: fallbackTitle,
    showButton = true,
    trackArticleId = false,
    buttonTag = `${cardType}_button`,
    showTitle,
    trackingData,
    cardFormat,
    image,
  } = props;
  const isWide = useMemo(() => cardFormat?.startsWith('wide'), [cardFormat]);
  const [cardImage, setCardImage] = useState<MediaImage['image']>(null);

  const { data: editorialStory } = useQuery({
    ...getEditorialByTagQuery(tag ?? `${options.competitionCode}.CARD.${cardType.replaceAll('-', '.')}`.toUpperCase()),
    placeholderData: teaser,
    enabled: !!fetchArticle,
  });

  useEffect(() => {
    setCardImage(image ?? editorialStory?.mainMedia?.image);
  }, [image, editorialStory?.mainMedia?.image]);

  const sponsorCode = useMemo(
    () => teaser?.sponsor?.type ?? teaser?.sponsors?.at(0) ?? cardType,
    [cardType, teaser?.sponsor?.type, teaser?.sponsors],
  );

  const { sponsor } = useSponsor(sponsorCode);

  const sponsorName = useMemo(() => !options.disableSponsors && sponsor?.code, [options.disableSponsors, sponsor]);

  const title = useMemo(
    () => (showTitle ? (editorialStory?.title ?? fallbackTitle) : ''),
    [editorialStory?.title, fallbackTitle, showTitle],
  );

  const tracking = useMemo(() => {
    return {
      id: trackArticleId ? editorialStory?.id : trackingData?.id,
      category: trackingData?.category,
      name: [sponsorName, trackingData?.name, title].filter(Boolean).join(' - '),
      item_category3: sponsor?.code ? 'sponsored' : undefined,
      item_category4: sponsor?.code,
    };
  }, [
    trackArticleId,
    editorialStory?.id,
    trackingData?.id,
    trackingData?.category,
    trackingData?.name,
    sponsorName,
    title,
    sponsor?.code,
  ]);

  return (
    <div className='card se' ref={trackCardElement(tracking)} data-event-label='se'>
      <BaseCardComponent
        {...props}
        sponsorCode={sponsorCode}
        sponsorPosition={isWide ? 'top-left' : 'default'}
        video={isVideoPlayer ? (video ?? editorialStory) : undefined}
        target={target}
        logoImg={logoImg}
        logoTxtFallback={logoTxtFallback ?? getTextFallBackTranslation(cardType)}
        fallbackImg={fallbackImg}
        title={title}
        buttonTag={showButton && buttonTag}
        theme='dark'
        image={cardImage}
      />
    </div>
  );
};

export default memo(SandEComponent);
